import { Component, DestroyRef, HostListener, TemplateRef, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  finalize,
  forkJoin,
  first,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  Subject,
  tap,
} from 'rxjs';
import { Constants } from 'src/app/core/constants';
import { SortChangeEvent } from 'src/app/core/directives/sortable-header.directive';
import { InventoryItemStatuses } from 'src/app/core/enums/inventory-item-statuses.enum';
import {
  InventoryItemType,
  getInventoryItemTypeLabelKey,
  isInventoryItemType,
} from 'src/app/core/enums/inventory-item-type.enum';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import { InventoryItem } from 'src/app/core/models/inventory/inventory-item.interface';
import { PagedList } from 'src/app/core/models/paged-list.interface';
import { Filter, RequestModel } from 'src/app/core/models/request-model.interface';
import { BusinessCentralConfigService } from 'src/app/core/services/business-central-config.service';
import { FileService } from 'src/app/core/services/file.service';
import { GoodsCategoriesService } from 'src/app/core/services/goods-categories.service';
import { ToastService } from 'src/app/core/services/toast.service';
import {
  CrmColumnVisibilityMap,
  CrmViewMode,
  CrmViewPreferencesService,
} from 'src/app/core/services/crm-view-preferences.service';
import { InventoryItemService } from 'src/app/core/services/inventory-item.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import {
  GoodsListItemViewModel,
  GoodsListPageViewModel,
} from './goods-list.view-model';
import {
  GoodsCategoryOptionViewModel,
  GoodsFilterModel,
  GoodsIsCompositeFilter,
  GoodsSourceFilter,
  GoodsStatusFilter,
  GoodsStockFilter,
  GoodsTypeFilter,
  GOODS_FILTER_FIELDS,
  createEmptyGoodsFilterModel,
} from './goods-filter.view-model';
import {
  GOODS_TABLE_COLUMNS,
  GoodsTableColumnDescriptor,
  GoodsTableColumnKey,
  GoodsTableColumnVisibility,
  buildDefaultColumnVisibility,
  extractPersistableVisibility,
  mergeColumnVisibility,
} from './goods-table-columns';

@Component({
  selector: 'app-goods',
  templateUrl: './goods.component.html',
  styleUrl: './goods.component.scss',
  standalone: false,
})
export class GoodsComponent {
  private readonly inventoryItemService = inject(InventoryItemService);
  private readonly fileService = inject(FileService);
  private readonly permissionService = inject(PermissionService);
  private readonly goodsCategoriesService = inject(GoodsCategoriesService);
  private readonly translateService = inject(TranslateService);
  private readonly viewPreferencesService = inject(CrmViewPreferencesService);
  private readonly businessCentralConfigService = inject(BusinessCentralConfigService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('bcSyncRequestFailedTpl')
  public bcSyncRequestFailedTpl!: TemplateRef<unknown>;

  public readonly currentLang: string;
  public readonly notFound = Constants.NotFound;
  public readonly uaDateFormat =
    Constants.DateFormats.get('ua-date') ?? 'dd.MM.yyyy';

  public readonly emptyGoodsListParam: Record<string, string> = {
    uk: 'товарів',
    en: 'goods',
  };

  public readonly typeOptions: readonly InventoryItemType[] = [
    'Product',
    'Service',
    'Hybrid',
  ];

  public searchTerm = '';
  public isLoading = false;
  public companyId = '';
  public isCreateGoodsModalOpen = false;
  public bcSyncAvailable = false;
  public isSyncing = false;
  public goodsView$: Observable<GoodsListPageViewModel> = EMPTY;
  public activeSortColumn = 'sku';
  public activeSortOrder: 'asc' | 'desc' = 'asc';

  public filterModel: GoodsFilterModel = createEmptyGoodsFilterModel();
  public categoryOptions: GoodsCategoryOptionViewModel[] = [];
  public activeFilterCount = 0;
  public viewMode: CrmViewMode = 'table';

  public readonly columns: readonly GoodsTableColumnDescriptor[] = GOODS_TABLE_COLUMNS;
  public visibleColumns: GoodsTableColumnVisibility = buildDefaultColumnVisibility();
  public isColumnMenuOpen = false;

  public readonly canCreateGoods$ = this.permissionService.userPermissions$.pipe(
    map((permissions) =>
      this.permissionService.hasPermission(
        permissions,
        ResourceTypes.Company,
        UserCompanyPermissions.Edit,
      ),
    ),
    shareReplay(),
  );

  private readonly searchChanges$ = new Subject<string>();
  private request: RequestModel = {
    page: 1,
    pageSize: 10,
    filters: [],
    sortColumn: 'sku',
    sortOrder: 'asc',
  };

  constructor() {
    this.route.parent?.parent?.params
      .pipe(
        first(),
        tap(params => {
          this.companyId = params['id'];
          this.viewMode = this.readPersistedViewMode(this.companyId);
          this.visibleColumns = this.readPersistedColumnVisibility(this.companyId);
          this.isLoading = true;
        }),
        tap(() => this.loadGoods()),
        tap(() => this.loadCategoryOptions()),
        tap(() => this.loadBusinessCentralAvailability()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.currentLang = this.translateService.currentLang ?? Constants.DefaultLanguage;

    this.searchChanges$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(value => {
          this.request.page = 1;
          this.request.searchTerm = value.trim() || undefined;
          this.isLoading = true;
          this.loadGoods();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public onPageChange(page: number): void {
    this.request.page = page;
    this.loadGoods();
  }

  public onChangePageSize(size: number): void {
    this.request.pageSize = size;
    this.request.page = 1;
    this.isLoading = true;
    this.loadGoods();
  }

  public onSearchChange(value: string): void {
    this.searchTerm = value;
    this.searchChanges$.next(value);
  }

  public onSortChange(event: SortChangeEvent): void {
    this.request.sortColumn = event.column;
    this.request.sortOrder = event.order;
    this.activeSortColumn = event.column;
    this.activeSortOrder = event.order;
    this.request.page = 1;
    this.isLoading = true;
    this.loadGoods();
  }

  public onCategoryFilterChange(value: string): void {
    this.filterModel = { ...this.filterModel, categoryId: value };
    this.applyFilters();
  }

  public onTypeFilterChange(value: string): void {
    this.filterModel = {
      ...this.filterModel,
      type: this.coerceTypeFilter(value),
    };
    this.applyFilters();
  }

  public onStatusFilterChange(value: string): void {
    this.filterModel = {
      ...this.filterModel,
      status: this.coerceStatusFilter(value),
    };
    this.applyFilters();
  }

  public onStockFilterChange(value: string): void {
    this.filterModel = {
      ...this.filterModel,
      stock: this.coerceStockFilter(value),
    };
    this.applyFilters();
  }

  public onSourceFilterChange(value: string): void {
    this.filterModel = {
      ...this.filterModel,
      source: this.coerceSourceFilter(value),
    };
    this.applyFilters();
  }

  public onIsCompositeFilterChange(value: string): void {
    this.filterModel = {
      ...this.filterModel,
      isComposite: this.coerceIsCompositeFilter(value),
    };
    this.applyFilters();
  }

  public resetFilters(): void {
    this.filterModel = createEmptyGoodsFilterModel();
    this.applyFilters();
  }

  public setViewMode(mode: CrmViewMode): void {
    if (this.viewMode === mode) {
      return;
    }
    this.viewMode = mode;
    if (this.companyId) {
      try {
        this.viewPreferencesService.setViewMode(this.companyId, mode);
      } catch {
        // Storage may be unavailable; keep the in-memory mode change.
      }
    }
  }

  private readPersistedViewMode(companyId: string): CrmViewMode {
    try {
      return this.viewPreferencesService.getViewMode(companyId);
    } catch {
      return 'table';
    }
  }

  private readPersistedColumnVisibility(
    companyId: string,
  ): GoodsTableColumnVisibility {
    try {
      const stored: CrmColumnVisibilityMap =
        this.viewPreferencesService.getColumnVisibility(companyId);
      return mergeColumnVisibility(stored);
    } catch {
      return buildDefaultColumnVisibility();
    }
  }

  public toggleColumnMenu(): void {
    this.isColumnMenuOpen = !this.isColumnMenuOpen;
  }

  public closeColumnMenu(): void {
    this.isColumnMenuOpen = false;
  }

  public toggleColumn(key: GoodsTableColumnKey): void {
    const descriptor = this.columns.find(column => column.key === key);
    if (!descriptor || descriptor.locked) {
      return;
    }

    this.visibleColumns = {
      ...this.visibleColumns,
      [key]: !this.visibleColumns[key],
    };

    if (!this.companyId) {
      return;
    }

    try {
      this.viewPreferencesService.setColumnVisibility(
        this.companyId,
        extractPersistableVisibility(this.visibleColumns),
      );
    } catch {
      // Storage may be unavailable; keep the in-memory change.
    }
  }

  @HostListener('document:keydown.escape')
  public onEscapeKey(): void {
    if (this.isColumnMenuOpen) {
      this.isColumnMenuOpen = false;
    }
  }

  public openCreateGoodsModal(): void {
    this.isCreateGoodsModalOpen = true;
  }

  public openImport(): void {
    if (!this.companyId) {
      return;
    }

    void this.router.navigate(['/companies', this.companyId, 'settings'], {
      queryParams: {
        section: 'imports',
        importEntity: 'inventory-items',
      },
    });
  }

  public closeCreateGoodsModal(): void {
    this.isCreateGoodsModalOpen = false;
  }

  public onCreateGoods(): void {
    this.closeCreateGoodsModal();
    this.isLoading = true;
    this.loadGoods();
  }

  public openProduct(id: string): void {
    this.router.navigate([id], { relativeTo: this.route });
  }

  public onSyncWithBusinessCentral(): void {
    if (!this.bcSyncAvailable || !this.companyId || this.isSyncing) {
      return;
    }

    this.isSyncing = true;

    // One combined endpoint runs items -> UoMs server-side in the correct FK order
    // and emits a single completion event, so the goods button fires exactly one
    // request and the user sees exactly one coalesced outcome.
    this.businessCentralConfigService
      .syncGoods(this.companyId, true)
      .pipe(
        catchError((err) => {
          console.error('BC sync request failed to start', err);
          this.toastService.show({
            template: this.bcSyncRequestFailedTpl,
            className: 'bg-danger text-light',
          });
          return EMPTY;
        }),
        finalize(() => {
          this.isSyncing = false;
        }),
        // The BC sync request takes 60-90s; if the user navigates away mid-flight the
        // bare .subscribe() would otherwise let the HTTP completion poke a destroyed
        // component (toggling isSyncing on a dead instance AND referencing
        // bcSyncRequestFailedTpl via @ViewChild which is undefined post-destroy).
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private loadBusinessCentralAvailability(): void {
    if (!this.companyId) {
      return;
    }

    this.businessCentralConfigService
      .getCompanyIntegration(this.companyId)
      .pipe(
        first(),
        tap(integration => (this.bcSyncAvailable = !!integration?.isConnected)),
        catchError(() => {
          this.bcSyncAvailable = false;
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private applyFilters(): void {
    this.request.filters = this.buildFilters(this.filterModel);
    this.activeFilterCount = this.countActiveFilters(this.filterModel);
    this.request.page = 1;
    this.isLoading = true;
    this.loadGoods();
  }

  private buildFilters(model: GoodsFilterModel): Filter[] {
    const filters: Filter[] = [];

    if (model.categoryId) {
      filters.push(this.toFilter(GOODS_FILTER_FIELDS.CategoryId, model.categoryId));
    }
    if (model.type) {
      filters.push(this.toFilter(GOODS_FILTER_FIELDS.Type, model.type));
    }
    if (model.status) {
      filters.push(this.toFilter(GOODS_FILTER_FIELDS.Status, model.status));
    }
    if (model.stock) {
      filters.push(this.toFilter(GOODS_FILTER_FIELDS.Stock, model.stock));
    }
    if (model.source) {
      filters.push(this.toFilter(GOODS_FILTER_FIELDS.Source, model.source));
    }
    if (model.isComposite) {
      filters.push(this.toFilter(GOODS_FILTER_FIELDS.IsComposite, model.isComposite));
    }

    return filters;
  }

  private toFilter(field: string, value: string): Filter {
    return { field, operator: 'eq', value, values: [] };
  }

  private countActiveFilters(model: GoodsFilterModel): number {
    let count = 0;
    if (model.categoryId) { count++; }
    if (model.type) { count++; }
    if (model.status) { count++; }
    if (model.stock) { count++; }
    if (model.source) { count++; }
    if (model.isComposite) { count++; }
    return count;
  }

  private coerceTypeFilter(value: string): GoodsTypeFilter {
    return isInventoryItemType(value) ? value : '';
  }

  private coerceStatusFilter(value: string): GoodsStatusFilter {
    const allowed: GoodsStatusFilter[] = ['active', 'blocked', 'archived', 'all'];
    return (allowed as string[]).includes(value) ? (value as GoodsStatusFilter) : '';
  }

  private coerceStockFilter(value: string): GoodsStockFilter {
    return value === 'in' || value === 'out' ? value : '';
  }

  private coerceSourceFilter(value: string): GoodsSourceFilter {
    return value === 'bc' || value === 'local' ? value : '';
  }

  private coerceIsCompositeFilter(value: string): GoodsIsCompositeFilter {
    return value === 'composite' || value === 'simple' ? value : '';
  }

  private loadCategoryOptions(): void {
    this.goodsCategoriesService
      .getCompanyGoodsCategories(this.companyId)
      .pipe(
        map(categories => this.flattenCategories(categories)),
        tap(options => (this.categoryOptions = options)),
        catchError(() => {
          this.categoryOptions = [];
          return of([] as GoodsCategoryOptionViewModel[]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private flattenCategories(
    categories: { id: string; title: string; children?: { id: string; title: string; children?: unknown[] }[] }[],
  ): GoodsCategoryOptionViewModel[] {
    const result: GoodsCategoryOptionViewModel[] = [];
    const walk = (
      nodes: { id: string; title: string; children?: { id: string; title: string; children?: unknown[] }[] }[],
    ): void => {
      for (const node of nodes) {
        result.push({ id: node.id, title: node.title });
        if (node.children && node.children.length) {
          walk(
            node.children as {
              id: string;
              title: string;
              children?: { id: string; title: string; children?: unknown[] }[];
            }[],
          );
        }
      }
    };
    walk(categories);
    return result;
  }

  private loadGoods(): void {
    this.goodsView$ = this.inventoryItemService
      .getCompanyInventoryItemsPaged(this.companyId, this.request)
      .pipe(
        switchMap(goods => this.mapToGoodsListPageViewModel$(goods)),
        tap(() => (this.isLoading = false)),
        catchError(error => {
          this.isLoading = false;
          throw error;
        }),
        shareReplay(1),
      );
  }

  private mapToGoodsListPageViewModel$(
    goods: PagedList<InventoryItem>,
  ): Observable<GoodsListPageViewModel> {
    const items$ = goods.items.length
      ? forkJoin(goods.items.map(item => this.mapToGoodsListItemViewModel$(item)))
      : of([]);

    return items$.pipe(
      map(items => ({
        items,
        totalCount: goods.totalCount,
        page: goods.page,
        pageSize: goods.pageSize,
      })),
    );
  }

  private mapToGoodsListItemViewModel$(
    item: InventoryItem,
  ): Observable<GoodsListItemViewModel> {
    const itemPath = `companies/${this.companyId}/inventory-items/${item.id}`;
    const currencyCode = (item.currencyCode ?? '').trim();

    return this.resolveMainImageUrl$(itemPath).pipe(
      map(imageUrl => ({
      id: item.id,
      number: item.number ?? null,
      displayName: item.displayName ?? null,
      displayName2: item.displayName2 ?? null,
      categoryTitle: item.categoryTitle ?? null,
      gtin: item.gtin ?? null,
      imageUrl,
      photoLabel: this.mapPhotoLabel(item),
      sourceLabelKey: this.mapSourceToLabelKey(item),
      isBusinessCentral: !!item.businessCentralId,
      itemTypeLabelKey: this.mapItemTypeToLabelKey(item.itemType),
      statusLabelKey: this.mapStatusToLabelKey(item),
      statusClass: this.mapStatusToClass(item),
      stock: item.stock ?? 0,
      unit: item.unit ?? null,
      unitPrice: item.unitPrice,
      unitCost: item.unitCost,
      currencyCode,
      hasCurrencyCode: currencyCode.length > 0,
      priceIncludesTaxLabelKey: item.priceIncludesTax
        ? 'BUTTONS.YES'
        : 'BUTTONS.NO',
      lastModifiedDateTime: item.lastModifiedDateTime,
      isArchived: item.isArchived ?? false,
      isComposite: item.isComposite === true,
      })),
    );
  }

  private mapItemTypeToLabelKey(itemType?: InventoryItemType | string | null): string | null {
    if (!itemType) {
      return null;
    }

    return isInventoryItemType(itemType) ? getInventoryItemTypeLabelKey(itemType) : null;
  }

  private mapSourceToLabelKey(item: InventoryItem): string {
    return item.businessCentralId
      ? 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.SOURCES.BUSINESS_CENTRAL'
      : 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.SOURCES.ORGLINES';
  }

  private mapStatusToLabelKey(item: InventoryItem): string {
    if (item.isArchived) {
      return 'INVENTORY_ITEM_STATUSES.ARCHIVED';
    }

    return item.status === InventoryItemStatuses.Blocked
      ? 'INVENTORY_ITEM_STATUSES.BLOCKED'
      : 'INVENTORY_ITEM_STATUSES.ACTIVE';
  }

  private mapStatusToClass(item: InventoryItem): string {
    if (item.isArchived) {
      return 'goods-page__status-badge--archived';
    }

    return item.status === InventoryItemStatuses.Blocked
      ? 'goods-page__status-badge--blocked'
      : 'goods-page__status-badge--active';
  }

  private mapPhotoLabel(item: InventoryItem): string {
    return item.displayName?.trim().charAt(0).toUpperCase() || 'I';
  }

  private resolveMainImageUrl$(itemPath: string): Observable<string> {
    return this.fileService.listFiles(itemPath).pipe(
      map(keys => this.findMainImagePath(keys, itemPath)),
      switchMap(mainImagePath =>
        mainImagePath
          ? this.fileService.getFileUrl(mainImagePath)
          : of(this.notFound),
      ),
      catchError(() => of(this.notFound)),
    );
  }

  private findMainImagePath(keys: string[], itemPath: string): string | null {
    const rootPathPrefix = `${itemPath}/`;

    return (
      keys.find(key => {
        if (!key.startsWith(rootPathPrefix)) {
          return false;
        }

        const fileName = key.slice(rootPathPrefix.length);
        if (fileName.includes('/')) {
          return false;
        }

        const extension = this.resolveFileExtension(fileName);
        const slotId = extension ? fileName.slice(0, -extension.length) : fileName;

        return slotId === 'title';
      }) ?? null
    );
  }

  private resolveFileExtension(fileName: string): string | undefined {
    const extensionIndex = fileName.lastIndexOf('.');
    if (extensionIndex <= 0) {
      return undefined;
    }

    return fileName.slice(extensionIndex).toLowerCase();
  }
}
